import { buildMetricsPalette } from '@/shared/constants/metricsPalette';
import {
  featureStatusOptions,
  releaseStatusOptions,
  suiteExecutionSummaryStatusOptions,
  testExecutionSummaryStatusOptions,
} from '@/shared/constants/statusOptions';
import { uiTokens } from '@/theme/uiTokens';
import {
  formatDuration,
  formatFrequency,
  formatPercent,
  formatStatusLabel,
  getChipPalette,
  getStatusChipKey,
  type StatusPieSlice,
} from '@/ui';
import { alpha } from '@/ui/primitives';
import type { MetricThreshold } from '@/shared/utils/metricsThresholds';
import type {
  BuildStatus,
  FeatureStatus,
  PipelineSummaryDto,
  ReleaseStatus,
  SuiteExecutionStatus,
  TestExecutionStatus,
} from '@flowqa/shared';

export type PipelineStatusKey = BuildStatus | 'none';
export type SuiteStatusKey = SuiteExecutionStatus | 'untested';
export type TestStatusKey = TestExecutionStatus | 'untested';

export const pipelineStatusDisplayOptions: Array<{ value: PipelineStatusKey; label: string }> = [
  { value: 'completed', label: formatStatusLabel('completed') },
  { value: 'failed', label: formatStatusLabel('failed') },
  { value: 'running', label: formatStatusLabel('running') },
  { value: 'queued', label: formatStatusLabel('queued') },
  { value: 'none', label: 'No Builds' },
];

export type ExecutionMetricDefinition<TSummary> = {
  title: string;
  format: (value: number | null | undefined) => string;
  getValue: (summary: TSummary | null | undefined) => number | null;
  getAccentValue?: (summary: TSummary | null | undefined) => number | null;
  threshold: MetricThreshold;
};

export const buildReleaseCounts = (
  releaseSummary:
    | { released?: number; overdue?: number; ready?: number; planned?: number; drafted?: number }
    | null
    | undefined,
): Partial<Record<ReleaseStatus, number>> => ({
  released: releaseSummary?.released ?? 0,
  overdue: releaseSummary?.overdue ?? 0,
  ready: releaseSummary?.ready ?? 0,
  planned: releaseSummary?.planned ?? 0,
  draft: releaseSummary?.drafted ?? 0,
});

export const buildFeatureCounts = (
  featureSummary:
    | { released?: number; ready?: number; planned?: number; drafted?: number }
    | null
    | undefined,
): Partial<Record<FeatureStatus, number>> => ({
  released: featureSummary?.released ?? 0,
  ready: featureSummary?.ready ?? 0,
  planned: featureSummary?.planned ?? 0,
  draft: featureSummary?.drafted ?? 0,
});

export const buildSuiteCounts = (
  suiteSummary:
    | { passed?: number; failed?: number; errored?: number; skipped?: number; untested?: number }
    | null
    | undefined,
): Partial<Record<SuiteStatusKey, number>> => ({
  passed: suiteSummary?.passed ?? 0,
  failed: suiteSummary?.failed ?? 0,
  errored: suiteSummary?.errored ?? 0,
  skipped: suiteSummary?.skipped ?? 0,
  untested: suiteSummary?.untested ?? 0,
});

export const buildTestCounts = (
  testSummary:
    | { passed?: number; failed?: number; errored?: number; skipped?: number; untested?: number }
    | null
    | undefined,
): Partial<Record<TestStatusKey, number>> => ({
  passed: testSummary?.passed ?? 0,
  failed: testSummary?.failed ?? 0,
  errored: testSummary?.errored ?? 0,
  skipped: testSummary?.skipped ?? 0,
  untested: testSummary?.untested ?? 0,
});

export const buildPipelineCounts = (
  pipelineSummary: PipelineSummaryDto | null | undefined,
): Partial<Record<PipelineStatusKey, number>> => ({
  completed: pipelineSummary?.statusCounts.completed ?? 0,
  failed: pipelineSummary?.statusCounts.failed ?? 0,
  running: pipelineSummary?.statusCounts.running ?? 0,
  queued: pipelineSummary?.statusCounts.queued ?? 0,
  none: pipelineSummary?.noBuilds ?? 0,
});

export const buildReleaseSlices = (
  releaseCounts: Partial<Record<ReleaseStatus, number>>,
): Array<StatusPieSlice<ReleaseStatus>> =>
  releaseStatusOptions.map((option) => ({
    key: option.value,
    label: option.label,
    value: releaseCounts[option.value] ?? 0,
    color: alpha(getChipPalette(getStatusChipKey(option.value)).icon, 0.8),
  }));

export const buildFeatureSlices = (
  featureCounts: Partial<Record<FeatureStatus, number>>,
): Array<StatusPieSlice<FeatureStatus>> =>
  featureStatusOptions.map((option) => ({
    key: option.value,
    label: option.label,
    value: featureCounts[option.value] ?? 0,
    color: alpha(getChipPalette(getStatusChipKey(option.value)).icon, 0.8),
  }));

export const buildSuiteSlices = (
  suiteCounts: Partial<Record<SuiteStatusKey, number>>,
): Array<StatusPieSlice<SuiteStatusKey>> => {
  const metricsPalette = buildMetricsPalette();
  const options = [
    ...suiteExecutionSummaryStatusOptions,
    { value: 'untested' as const, label: 'Untested' },
  ];
  return options.map((option) => ({
    key: option.value,
    label: option.label,
    value: suiteCounts[option.value] ?? 0,
    color:
      option.value === 'untested'
        ? uiTokens.statusColors.neutral
        : option.value === 'passed'
          ? metricsPalette.pass
          : option.value === 'failed'
            ? metricsPalette.fail
            : option.value === 'errored'
              ? metricsPalette.error
              : metricsPalette.skip,
  }));
};

export const buildTestSlices = (
  testCounts: Partial<Record<TestStatusKey, number>>,
): Array<StatusPieSlice<TestStatusKey>> => {
  const metricsPalette = buildMetricsPalette();
  const options = [
    ...testExecutionSummaryStatusOptions,
    { value: 'untested' as const, label: 'Untested' },
  ];
  return options.map((option) => ({
    key: option.value,
    label: option.label,
    value: testCounts[option.value] ?? 0,
    color:
      option.value === 'untested'
        ? uiTokens.statusColors.neutral
        : option.value === 'passed'
          ? metricsPalette.pass
          : option.value === 'failed'
            ? metricsPalette.fail
            : option.value === 'errored'
              ? metricsPalette.error
              : metricsPalette.skip,
  }));
};

export const buildPipelineSlices = (
  pipelineCounts: Partial<Record<PipelineStatusKey, number>>,
): Array<StatusPieSlice<PipelineStatusKey>> => {
  return pipelineStatusDisplayOptions.map((option) => ({
    key: option.value,
    label: option.label,
    value: pipelineCounts[option.value] ?? 0,
    color:
      option.value === 'completed'
        ? uiTokens.statusColors.green
        : option.value === 'failed'
          ? uiTokens.statusColors.red
          : option.value === 'running'
            ? uiTokens.statusColors.blue
            : uiTokens.statusColors.neutral,
  }));
};

export const formatPercentValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return `${formatPercent(value)}%`;
};

export const formatFrequencyValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return formatFrequency(value);
};

export const formatDurationValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return formatDuration(value);
};

export const formatMttfValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return formatDuration(value * 1000);
};

export const formatAverageValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
